<section class="search-page" [class.has-detail]="selectedOrder()">

  <!-- ── Header ───────────────────────────────────────────────────────────── -->
  <div class="page-header">
    <h1>All Orders</h1>
  </div>

  <!-- ── Search bar ───────────────────────────────────────────────────────── -->
  <div class="search-bar">
    <mat-icon class="search-icon">search</mat-icon>
    <input
      class="search-input"
      type="search"
      [(ngModel)]="searchQuery"
      placeholder="Search by order ID (e.g. ORD-00001)..."
      (keydown.enter)="onSearch()"
    />
    <button class="ghost-btn ghost-btn--small search-btn" type="button" (click)="onSearch()">
      Search
    </button>
  </div>

  <!-- ── Body ─────────────────────────────────────────────────────────────── -->
  <div class="page-body">

    <!-- Table panel -->
    <div class="table-panel">
      @if (loading()) {
        <div class="loading">
          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
        </div>
      } @else if (error()) {
        <div class="empty error-text">{{ error() }}</div>
      } @else {
        <div class="table-wrap">
          @if (orders().length) {
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th class="sort-th" [class.sort-th--active]="sortField === 'status'" (click)="toggleSort('status')">
                    Status
                    <mat-icon class="sort-icon">{{ sortField === 'status' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
                  </th>
                  <th class="sort-th" [class.sort-th--active]="sortField === 'city_code'" (click)="toggleSort('city_code')">
                    City
                    <mat-icon class="sort-icon">{{ sortField === 'city_code' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
                  </th>
                  <th>Provider</th>
                  <th class="sort-th" [class.sort-th--active]="sortField === 'updated_at'" (click)="toggleSort('updated_at')">
                    Updated
                    <mat-icon class="sort-icon">{{ sortField === 'updated_at' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
                  </th>
                  <th class="actions-th"></th>
                </tr>
              </thead>
              <tbody>
                @for (order of orders(); track order.id) {
                  <tr [class.row--selected]="selectedOrder()?.id === order.id">
                    <td class="code-cell">{{ order.order_code }}</td>
                    <td class="name-cell">{{ customerName(order) }}</td>
                    <td class="status-cell">
                      <span class="status-chip status-chip--{{ statusTone(order.status) }}">
                        {{ statusLabel(order.status) }}
                      </span>
                    </td>
                    <td class="city-cell">{{ order.city_code }}</td>
                    <td class="provider-cell">{{ providerLabel(order) }}</td>
                    <td class="date-cell">{{ order.updated_at | date: 'MMM d, y, HH:mm' }}</td>
                    <td class="actions-cell">
                      <button type="button" class="ghost-btn ghost-btn--small" (click)="openDetail(order)">Open</button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <div class="empty">No closed orders found.</div>
          }
        </div>
      }
    </div>

    <!-- Detail panel -->
    @if (selectedOrder(); as order) {
      <div class="detail-panel">

        <!-- Header -->
        <div class="detail-header">
          <div class="detail-header-left">
            <span class="detail-order-code">{{ order.order_code }}</span>
            <span class="status-chip status-chip--{{ statusTone(order.status) }}">{{ statusLabel(order.status) }}</span>
          </div>
          <button type="button" class="detail-close-btn" aria-label="Close detail" (click)="closeDetail()">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div class="detail-body">

          <!-- ── Summary ──────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">receipt_long</mat-icon>
              <h2 class="section-title">Order</h2>
            </div>
            <div class="fields-grid">

              <div class="field">
                <span class="field-label">City</span>
                <span class="field-value">{{ order.city_code }}</span>
              </div>

              <div class="field">
                <span class="field-label">Service model</span>
                <span class="field-value">{{ serviceModelLabel(order.service_model) }}</span>
              </div>

              @if (order.category) {
                <div class="field">
                  <span class="field-label">Category</span>
                  <span class="field-value">{{ order.category }}</span>
                </div>
              }

              @if (order.service_name) {
                <div class="field">
                  <span class="field-label">Service</span>
                  <span class="field-value">{{ order.service_name }}</span>
                </div>
              }

              @if (order.scheduled_at) {
                <div class="field">
                  <span class="field-label">Scheduled</span>
                  <span class="field-value">{{ order.scheduled_at | date: 'MMM d, y, HH:mm' }}</span>
                </div>
              }

              @if (order.estimated_duration_minutes) {
                <div class="field">
                  <span class="field-label">Duration</span>
                  <span class="field-value">{{ order.estimated_duration_minutes }} min</span>
                </div>
              }

              <div class="field">
                <span class="field-label">Created</span>
                <span class="field-value field-value--muted">{{ order.created_at | date: 'MMM d, y, HH:mm' }}</span>
              </div>

              <div class="field">
                <span class="field-label">Updated</span>
                <span class="field-value field-value--muted">{{ order.updated_at | date: 'MMM d, y, HH:mm' }}</span>
              </div>

            </div>
          </div>

          <!-- ── Customer ─────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">person</mat-icon>
              <h2 class="section-title">Customer</h2>
            </div>
            <div class="fields-stack">
              <div class="field">
                <span class="field-label">Name</span>
                <span class="field-value">{{ customerName(order) }}</span>
              </div>
              @if (order.handoff?.customer_phone) {
                <div class="field">
                  <span class="field-label">Phone</span>
                  <span class="field-value">{{ order.handoff!.customer_phone }}</span>
                </div>
              }
              @if (order.address) {
                <div class="field">
                  <span class="field-label">Service address</span>
                  <span class="field-value">{{ order.address }}</span>
                </div>
              }
              @if (order.address_access_notes) {
                <div class="field">
                  <span class="field-label">Access notes</span>
                  <span class="field-value">{{ order.address_access_notes }}</span>
                </div>
              }
            </div>
          </div>

          <!-- ── Provider ─────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">handyman</mat-icon>
              <h2 class="section-title">Provider</h2>
            </div>
            <div class="fields-stack">
              @if (order.providers) {
                <div class="field">
                  <span class="field-label">Name</span>
                  <a class="field-link" [routerLink]="['/providers', order.providers.id]">{{ order.providers.name }}</a>
                </div>
                @if (order.providers.phone) {
                  <div class="field">
                    <span class="field-label">Phone</span>
                    <span class="field-value">{{ order.providers.phone }}</span>
                  </div>
                }
                @if (order.providers.email) {
                  <div class="field">
                    <span class="field-label">Email</span>
                    <span class="field-value">{{ order.providers.email }}</span>
                  </div>
                }
              } @else {
                <div class="field">
                  <span class="field-label">Assignment</span>
                  <span class="field-value field-value--muted">{{ providerLabel(order) }}</span>
                </div>
              }
              @if (order.provider_notes) {
                <div class="field">
                  <span class="field-label">Provider notes</span>
                  <span class="field-value">{{ order.provider_notes }}</span>
                </div>
              }
            </div>
          </div>

          <!-- ── Payment ──────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">payments</mat-icon>
              <h2 class="section-title">Payment</h2>
            </div>
            <div class="fields-grid">

              <div class="field">
                <span class="field-label">Type</span>
                <span class="field-value">{{ paymentTypeLabel(order.payment_type) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Status</span>
                <span class="field-value">{{ paymentStatusLabel(order.payment_status) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Quote</span>
                <span class="field-value">{{ centsToDollars(order.quote_amount_cents) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Amount</span>
                <span class="field-value">{{ centsToDollars(order.payment_amount_cents) }}</span>
              </div>

              @if (order.stripe_payment_link_url) {
                <div class="field field--wide">
                  <span class="field-label">Payment link</span>
                  <a class="field-link" [href]="order.stripe_payment_link_url" target="_blank" rel="noopener">
                    {{ order.stripe_payment_link_url }}
                  </a>
                </div>
              }

            </div>
          </div>

          <!-- ── Notes ────────────────────────────────────────────────────── -->
          @if (order.internal_notes || order.completion_notes || order.cancelled_reason) {
            <div class="section-card">
              <div class="section-header">
                <mat-icon class="section-icon">notes</mat-icon>
                <h2 class="section-title">Notes</h2>
              </div>
              <div class="fields-stack">
                @if (order.internal_notes) {
                  <div class="field">
                    <span class="field-label">Internal notes</span>
                    <p class="notes-text">{{ order.internal_notes }}</p>
                  </div>
                }
                @if (order.completion_notes) {
                  <div class="field">
                    <span class="field-label">Completion notes</span>
                    <p class="notes-text">{{ order.completion_notes }}</p>
                  </div>
                }
                @if (order.cancelled_reason) {
                  <div class="field">
                    <span class="field-label">Cancelled reason</span>
                    <p class="notes-text">{{ order.cancelled_reason }}</p>
                  </div>
                }
              </div>
            </div>
          }

        </div>
      </div>
    }

  </div>

</section>
